import _ from 'underscore';
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';
import { KendoStringDatePicker } from '@bingads-webui-react/kendo-date-picker';

const dateMethodOptions = {
  raw: 'yyyy-MM-dd',
};

const StartAndEndDate = React.memo(({
  value, onChange, i18n, id, className, updateValidation,
  startDateClassName, endDateClassName, startAndEndDateSeparator,
}) => {
  const [startDate, setStartDate] = useState(value.PromotionStartDate);
  const [endDate, setEndDate] = useState(value.PromotionEndDate);
  const [isPromotionStartDateInvalid, setIsPromotionStartDateInvalid] = useState(false);
  const [isPromotionEndDateInvalid, setIsPromotionEndDateInvalid] = useState(false);

  const validate = useCallback(() => {
    const startDateObject =
      (startDate && i18n.parseDate(startDate.substr(0, 10), dateMethodOptions)) ||
      startDate;
    const endDateObject =
      (endDate && i18n.parseDate(endDate.substr(0, 10), dateMethodOptions)) ||
      endDate;

    const messages = [];

    if (endDateObject && startDateObject && startDateObject > endDateObject) {
      messages.push(i18n.getString(_TL_('End date cannot be earlier than start date')));
    }

    // Raw date is non-empty string instead of Date object if invalid date entered.
    if (isPromotionStartDateInvalid || (!_.isEmpty(startDate) && startDateObject === null)) {
      messages.push(i18n.getString(_TL_('Invalid start date')));
    }

    if (isPromotionEndDateInvalid || (!_.isEmpty(endDate) && endDateObject === null)) {
      messages.push(i18n.getString(_TL_('Invalid end date')));
    }

    return messages;
  }, [startDate, endDate, i18n, isPromotionStartDateInvalid, isPromotionEndDateInvalid]);

  useEffect(() => {
    setStartDate(value.PromotionStartDate);
    setEndDate(value.PromotionEndDate);
  }, [value.PromotionStartDate, value.PromotionEndDate]);

  useEffect(() => {
    const errorMessages = validate();
    updateValidation(errorMessages);
  }, [startDate, endDate, validate, updateValidation]);

  useEffect(() => {
    onChange({ PromotionStartDate: startDate, PromotionEndDate: endDate });
  }, [onChange, startDate, endDate]);

  const onStartTimeChange = useCallback((PromotionStartDate) => {
    setStartDate(PromotionStartDate);
    setIsPromotionStartDateInvalid(false);
  }, []);

  const onEndTimeChange = useCallback((PromotionEndDate) => {
    setEndDate(PromotionEndDate);
    setIsPromotionEndDateInvalid(false);
  }, []);

  const onStartTimeError = useCallback((/* error */) => {
    setStartDate(undefined);
    setIsPromotionStartDateInvalid(true);
  }, []);

  const onEndTimeError = useCallback((/* error */) => {
    setEndDate(undefined);
    setIsPromotionEndDateInvalid(true);
  }, []);

  const usedStartTime = startDate ? startDate.substr(0, 10) : startDate;
  const usedEndTime = endDate ? endDate.substr(0, 10) : endDate;

  return (
    <div className={className} id={id}>
      <div className={startDateClassName} id="start-date">
        <KendoStringDatePicker
          i18n={i18n}
          onChange={onStartTimeChange}
          onError={onStartTimeError}
          value={usedStartTime}
          dateMethodOptions={dateMethodOptions}
        />
      </div>
      <span className={startAndEndDateSeparator}>-</span>
      <div className={endDateClassName} id="end-date">
        <KendoStringDatePicker
          i18n={i18n}
          onChange={onEndTimeChange}
          onError={onEndTimeError}
          value={usedEndTime}
          dateMethodOptions={dateMethodOptions}
        />
      </div>
    </div>
  );
// eslint-disable-next-line max-len
}, (prevProps, nextProps) => prevProps.value.PromotionStartDate === nextProps.value.PromotionStartDate
  && prevProps.value.PromotionEndDate === nextProps.value.PromotionEndDate);

StartAndEndDate.propTypes = {
  id: PropTypes.string.isRequired,
  className: PropTypes.string.isRequired,
  startDateClassName: PropTypes.string.isRequired,
  endDateClassName: PropTypes.string.isRequired,
  startAndEndDateSeparator: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.shape({}),
  updateValidation: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

StartAndEndDate.defaultProps = {
  value: undefined,
};

export const StartAndEndDateWithValidation =
  withError(withCustomValidation(StartAndEndDate));
